import React, { useState, useEffect } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import {
  getUpcomingInvoice,
  resetInvoice,
  setPendingUpdateData,
  submitInvoice,
  resetSubscription,
  clearSession,
  clearErrors,
} from "./dashboardSlice";
import { IAppState } from "../../helpers/store";
import NewSubscriptionData from "./NewSubscriptionData";
import {
  IInvoiceData,
  INewInvoiceData,
  ISubscriptionData,
} from "../../services/dashboard.services";
import ProrationData from "./ProrationData";
import NewMonthlySubscriptionData from "./NewMonthlySubscriptionData";
import SubscriptionTotalData from "./SubscriptionTotalData";
import CurrentSubscriptionData from "./CurrentSubscriptionData";
import { useHistory } from "react-router-dom";
import { ReducerStates, Views } from "../../helpers/constants";
import { useTranslation } from "react-i18next";
import InvoiceTotalData from "./InvoiceTotalData";
import { getPlanDateString } from "./PlanData";
import { RedirectErrors } from "../../services/constants";
import { DateTimeISO, DateTimeNow } from "../../helpers/datetime";
import { ErrorDialog } from "../common/ErrorRetryDialog";
import ConfirmPlanPolicy from "./ConfirmPlanPolicy";

// Component styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      display: "flex",
      alignItems: "left",
      flexDirection: "column",
      width: "80%",
    },
    continueButton: {
      padding: "8px 50px",
      width: "180px",
      height: "42px",
    },
    backButtonSmall: {
      width: "180px",
      height: "42px",
    },
    invoiceButtons: {
      marginTop: "10px",
      width: "80%",
      height: "45px",
    },
    versionInput: {
      width: "100%",
      marginBottom: "15px",
    },
    updatePlanTitle: {
      color: theme.palette.dark.main,
      fontWeight: "bold",
      fontSize: "1.5rem",
      paddingBottom: "50px",
    },
    ConfirmPlanTitle: {
      color: theme.palette.dark.main,
      fontWeight: "bold",
      fontSize: "1.5rem",
      paddingBottom: "30px",
    },
    planTitle: {
      color: theme.palette.dark.main,
      display: "flex",
      alignItems: "left",
      paddingBottom: "30px",
      fontSize: "0.9rem",
      fontWeight: "bold",
    },
    invoiceTitle: {
      color: theme.palette.dark.main,
      fontSize: "0.8rem",
      marginBottom: "20px",
    },
    continueContainer: {
      justifyContent: "end",
      alignContent: "center",
      height: "60px",
      marginTop: "20px",
    },
    backContainer: {
      justifyContent: "end",
      alignContent: "center",
      height: "60px",
    },
    continueGrid: {
      display: "flex",
      width: "525px",
    },
    totalAmount: {
      color: theme.palette.dark.main,
      marginTop: "20px",
      fontWeight: "bold",
    },
    totalAmountChanged: {
      marginTop: "20px",
      fontWeight: "bold",
      color: theme.palette.primary.main,
    },
    paymentBox: {
      height: "25px",
      width: "80%",
      marginBottom: "25px",
    },
    paymentText: {
      fontSize: "0.75rem",
      textAlign: "end",
    },
  })
);

export function getDisplayAmount(amount: number, country: string = "US") {
  const countryToCurrency: Record<string, string> = {
    US: "USD",
    CA: "CAD",
  };

  const currency = countryToCurrency[country] || "USD";

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });

  return formatter.format(amount);
}

export default function SubscriptionForm() {
  /* ---- Hooks ---- */
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const [open, setOpen] = React.useState(false);

  /* ---- Selectors ---- */
  const invoiceData = useSelector(
    (state: IAppState) => state.dashboardState.invoiceData
  );

  const invoiceLoaded = useSelector(
    (state: IAppState) => state.dashboardState.invoiceLoaded
  );

  const invoiceSubmitted = useSelector(
    (state: IAppState) => state.dashboardState.invoiceSubmitted
  );

  const selectedSubscription = useSelector(
    (state: IAppState) => state.dashboardState.selectedSubscription
  );

  // const country =
  //   selectedSubscription?.subscription_items?.[0]?.country ?? "US";

  const dashboardError = useSelector(
    (state: IAppState) => state.dashboardState.errorCode
  );

  const dashboardState = useSelector(
    (state: IAppState) => state.dashboardState.state
  );

  const token = useSelector((state: IAppState) => state.appState.tokenObj);

  const pendingUpdateData = useSelector(
    (state: IAppState) => state.dashboardState.pendingUpdateData
  );

  const defaultProductData: { [key: string]: string } = {};
  const defaultItemQuantities: { [key: string]: number } = {};
  const invoice = invoiceData ? invoiceData[0] : null;

  /* ---- State ---- */
  const [itemQuantity, setItemQuantity] = useState(defaultItemQuantities);
  const [productData, setProductData] = useState(defaultProductData);
  const [country, setCountry] = useState<string>("US");

  const loadSubscriptionData = (
    subscriptionData: ISubscriptionData | undefined
  ) => {
    let itemQuantities: { [key: string]: number } = {};
    if (subscriptionData) {
      subscriptionData.subscription_items.forEach((item) => {
        itemQuantities[item.id] = item.quantity;
        productData[item.product_id] = item.product.name;
      });
    }

    return itemQuantities;
  };

  const loadProductData = (subscriptionData: ISubscriptionData | undefined) => {
    let productData: { [key: string]: string } = {};
    if (subscriptionData) {
      subscriptionData.subscription_items.forEach((item) => {
        productData[item.product_id] = item.product.name;
      });
    }

    return productData;
  };

  const loadProductCountry = (
    subscriptionData: ISubscriptionData | undefined
  ): string => {
    if (subscriptionData && subscriptionData.subscription_items.length > 0) {
      return subscriptionData.subscription_items[0].product.country;
    }
    return "US"; // fallback default
  };

  /* ---- Effects ---- */
  useEffect(() => {
    setProductData(loadProductData(selectedSubscription));
    setCountry(loadProductCountry(selectedSubscription));
    setItemQuantity(loadSubscriptionData(selectedSubscription));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubscription]);

  useEffect(() => {
    if (invoiceSubmitted) {
      history.push(Views.SUBSCRIPTION_CONFIRMATION);
    }
  });

  useEffect(() => {
    if (dashboardState === ReducerStates.FAILED) {
      let redirectErrorDetected = false;
      Object.keys(RedirectErrors).forEach((key) => {
        if (
          RedirectErrors[key as keyof typeof RedirectErrors] === dashboardError
        ) {
          dispatch(clearSession());
          redirectErrorDetected = true;
        }
      });
      if (!redirectErrorDetected) {
        dispatch(clearErrors());
        setOpen(true);
      }
    }
  }, [dashboardState, dashboardError, dispatch]);

  useEffect(() => {
    let isExpired = false;
    if (token && token.access_expiration) {
      const now = DateTimeNow();
      const expiration = DateTimeISO(token.access_expiration);
      isExpired = now > expiration;
    }
    if (isExpired) {
      dispatch(clearSession());
    }
  });

  const handleSetItemQuantity = (planId: string, quantity: number) => {
    setItemQuantity((prev) => ({
      ...prev,
      [planId]: quantity,
    }));
  };

  const handleGetInvoice = () => {
    setOpen(false);
    let invoices: IInvoiceData[] = [];
    let subscription_items: {
      id: string;
      quantity: number;
      new_item: boolean;
      price: string;
    }[] = [];

    if (selectedSubscription) {
      selectedSubscription.subscription_items.forEach((item) => {
        if (item.quantity !== itemQuantity[item.id]) {
          subscription_items.push({
            id: item.id,
            quantity: itemQuantity[item.id],
            new_item: item.new_item,
            price: item.product.price,
          });
        }
      });
      invoices.push({
        customer_id: selectedSubscription.customer_id,
        subscription_id: selectedSubscription.subscription_id,
        subscription_items: subscription_items,
      });
      dispatch(setPendingUpdateData(invoices));
      dispatch(getUpcomingInvoice(invoices));
    }
  };

  const handleSubmitInvoice = () => {
    setOpen(false);
    if (pendingUpdateData) {
      dispatch(submitInvoice(pendingUpdateData));
    }
  };

  /* ---- Utils ---- */
  const handleResetInvoice = () => {
    dispatch(resetInvoice());
  };

  const handleResetSubscription = () => {
    dispatch(resetSubscription());
    history.push(Views.SUBSCRIPTION);
  };

  let invoiceAmount = 0;
  if (invoice) {
    invoice.subscription_items.forEach((subItem) => {
      if (subItem.current_quantity !== subItem.new_quantity) {
        let amountDiff = subItem.new_amount + subItem.current_amount;
        invoiceAmount += amountDiff;
      }
    });
  }

  function getTotal(
    subscriptionData: ISubscriptionData | undefined,
    itemQuantity: { [key: string]: number } | null
  ) {
    if (!subscriptionData) {
      return getDisplayAmount(0);
    }
    let total = 0;
    subscriptionData.subscription_items.forEach((subItem) => {
      const quantity =
        itemQuantity && itemQuantity[subItem.id] !== undefined
          ? itemQuantity[subItem.id]
          : subItem.quantity;
      total += subItem.amount * quantity;
    });
    return getDisplayAmount(total / 100, country);
  }

  function getInvoiceTotal(
    subscriptionData: INewInvoiceData,
    itemQuantity: { [key: string]: number } | null
  ) {
    let total = 0;
    subscriptionData.subscription_items.forEach((subItem) => {
      total += subItem.base_amount * subItem.new_quantity;
    });
    return getDisplayAmount(total / 100, country);
  }

  function isQuantityChanged() {
    if (!itemQuantity || !selectedSubscription) {
      return false;
    }
    let isChanged = false;
    selectedSubscription.subscription_items.forEach((subItem) => {
      let newQuantity =
        itemQuantity[subItem.id] !== undefined
          ? itemQuantity[subItem.id]
          : subItem.quantity;

      if (subItem.quantity !== newQuantity) {
        isChanged = true;
      }
    });

    return isChanged;
  }

  const handleErrorClose = () => {
    setOpen(false);
  };

  return (
    <React.Fragment>
      {!invoiceSubmitted && (
        <React.Fragment>
          {!invoiceLoaded && (
            <form
              id="request-code-form"
              noValidate
              className={classes.formContainer}
              autoComplete="off"
            >
              <Typography className={classes.updatePlanTitle}>
                {t("update_plan")}
              </Typography>
              <Grid container>
                <Grid item xs={6}>
                  <Typography className={classes.planTitle}>
                    {t("current_plan")}
                  </Typography>
                  {selectedSubscription && (
                    <CurrentSubscriptionData
                      subscriptionData={selectedSubscription}
                    />
                  )}
                  <Typography className={classes.totalAmount}>
                    {getTotal(selectedSubscription, null) +
                      " " +
                      t("per_month")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className={classes.planTitle}>
                    {t("new_plan")}
                  </Typography>
                  {selectedSubscription && (
                    <NewSubscriptionData
                      subscriptionData={selectedSubscription}
                      itemQuantity={itemQuantity}
                      handleSetItemQuantity={handleSetItemQuantity}
                    />
                  )}
                  <Typography
                    className={
                      isQuantityChanged()
                        ? classes.totalAmountChanged
                        : classes.totalAmount
                    }
                  >
                    {getTotal(selectedSubscription, itemQuantity) +
                      " per Month"}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container className={classes.continueContainer}>
                <Grid item xs={6} className={classes.continueGrid}>
                  <Button
                    onClick={handleGetInvoice}
                    variant={!isQuantityChanged() ? "outlined" : "contained"}
                    color="primary"
                    size="large"
                    className={classes.continueButton}
                    disabled={!isQuantityChanged()}
                  >
                    {t("continue")}
                  </Button>
                </Grid>
              </Grid>
              <Grid container className={classes.backContainer}>
                <Grid item xs={6} className={classes.continueGrid}>
                  <Button
                    onClick={handleResetSubscription}
                    variant={"outlined"}
                    color="default"
                    size="large"
                    className={classes.backButtonSmall}
                  >
                    <Typography>{t("back")}</Typography>
                  </Button>
                </Grid>
              </Grid>
              <ErrorDialog
                toggle={open}
                onClose={handleErrorClose}
                onRetry={handleGetInvoice}
              />
            </form>
          )}

          {invoiceLoaded && (
            <form
              id="request-code-form"
              noValidate
              className={classes.formContainer}
              autoComplete="off"
            >
              <Typography className={classes.ConfirmPlanTitle}>
                {t("confirm_plan_updates")}
              </Typography>
              <Typography className={classes.invoiceTitle}>
                {t("new_monthly_plan")}
              </Typography>
              {invoice && (
                <React.Fragment>
                  <NewMonthlySubscriptionData
                    invoiceData={invoice}
                    productData={productData}
                    country={country}
                  />
                  <InvoiceTotalData
                    invoiceTotal={getInvoiceTotal(invoice, null)}
                  ></InvoiceTotalData>
                  <Box className={classes.paymentBox}>
                    <Typography className={classes.paymentText}>
                      {t("start_payment") +
                        " " +
                        getPlanDateString(invoice.period_end)}
                    </Typography>
                  </Box>
                  <Typography className={classes.invoiceTitle}>
                    {t("pro_rated_adjustment")}
                  </Typography>
                  <ProrationData
                    invoiceData={invoice}
                    productData={productData}
                    country={country}
                  />
                </React.Fragment>
              )}
              <SubscriptionTotalData
                amountDue={invoiceAmount}
                country={country}
              ></SubscriptionTotalData>
              <ConfirmPlanPolicy />
              <Grid>
                <Button
                  onClick={handleSubmitInvoice}
                  variant="outlined"
                  color="primary"
                  size="large"
                  className={classes.invoiceButtons}
                >
                  <Typography>{t("confirm")}</Typography>
                </Button>
              </Grid>
              <Grid>
                <Button
                  onClick={handleResetInvoice}
                  variant="outlined"
                  color="default"
                  size="large"
                  className={classes.invoiceButtons}
                >
                  <Typography>{t("back")}</Typography>
                </Button>
              </Grid>
              <ErrorDialog
                toggle={open}
                onClose={handleErrorClose}
                onRetry={handleSubmitInvoice}
              />
            </form>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}
